<template>
  <button
    @click="onToggleMode"
    class="hidden sm:flex z-10 items-center justify-center w-6 h-6 rounded bg-gray-200 text-gray-700 border border-gray-400 fixed top-8 transition-all duration-200 ease-in-out-circ"
    :class="[isCollapsed ? 'float-right-sm' : 'float-right-lg']"
  >
    <ChevronDoubleRightIcon
      v-if="inCollapsedMode"
      class="w-3 h-3"
    />
    <ChevronDoubleLeftIcon
      v-else
      class="w-3 h-3"
    />
  </button>
  <nav
    ref="target"
    class="fixed z-40 left-0 top-0 h-dvh flex flex-col gap-y-14 py-6 text-sm whitespace-nowrap overflow-x-hidden overflow-y-auto bg-navy-400 text-gray-200 transition-all duration-200 ease-in-out-circ"
    :class="[isCollapsed ? 'w-20' : 'w-56', isHidden ? 'hidden' : 'fixed']"
    @mouseenter="onMouseEnterNav"
    @mouseleave="onMouseleaveNav"
  >
    <div class="px-4">
      <SalveLogo
        :brand-mark-only="isCollapsed"
        width="166"
        height="42"
        alt="Salve Logo"
        class="min-w-[166px]"
      />
      <button
        type="button"
        class="sm:hidden absolute top-2 right-2"
        @click="onMobileClose"
      >
        <XMarkIcon class="w-5 h-5" />
      </button>
    </div>
    <div class="flex-1">
      <ol class="flex flex-col gap-y-2">
        <NavItem
          v-for="item in navigation"
          :item="item"
          :show-label="!isCollapsed"
        />
      </ol>
    </div>
    <footer class="px-4">
      <InertiaLink
        as="button"
        :href="route('user.edit')"
        class="h-12 border border-navy-200 font-bold rounded px-2 w-full flex justify-center items-center overflow-hidden"
      >
        <template v-if="hasUsername">
          <transition
            name="name-button"
            mode="out-in"
          >
            <span v-if="isCollapsed">{{ userInitials }}</span>
            <span
              v-else
              class="flex items-center gap-x-2"
            >
              <UserCircleIcon class="w-6 h-6" />
              {{ userFirstName }} {{ userLastName }}
            </span>
          </transition>
        </template>
        <template v-else>
          <transition
            name="name-button"
            mode="out-in"
          >
            <span v-if="isCollapsed">
              <UserCircleIcon class="w-6 h-6" />
            </span>
            <span v-else>My Account</span>
          </transition>
        </template>
      </InertiaLink>
    </footer>
  </nav>
</template>

<style scoped>
.float-right-sm {
  left: calc(theme('spacing.20') + 10px);
}
.float-right-lg {
  left: calc(theme('spacing.56') + 10px);
}
.min-w-\[166px\] {
  min-width: 166px;
}
.name-button-enter-active,
.name-button-leave-active {
  transition: opacity 150ms ease;
}
.name-button-enter-from,
.name-button-leave-to {
  opacity: 0;
}
</style>

<script setup>
import { ref, computed } from 'vue'
import { usePage, Link as InertiaLink } from '@inertiajs/vue3'
import { onClickOutside } from '@vueuse/core'
import { getNavigation } from '@/Layouts/Components/generateNavigation'
import SalveLogo from '@/Components/Assets/SalveLogo.vue'
import NavItem from '@/Components/Navigation/NavItem.vue'
import { useNavigation } from '@/Components/Navigation/navigation'
import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon, XMarkIcon, UserCircleIcon } from '@heroicons-2/vue/24/outline'

const props = defineProps({
  userInitials: {
    type: String,
    default: ''
  },
  userFirstName: {
    type: String,
    default: ''
  },
  userLastName: {
    type: String,
    default: ''
  }
})

const hasUsername = computed(() => {
  return props.userInitials !== ''
})

const page = usePage()
const navigation = getNavigation(route, page.props.auth)

const {
  mode,
  position,
  onToggleMode,
  onMouseEnterNav,
  onMouseleaveNav,
  onMobileClose,
  onClickOutsideNav,
  isCollapsed,
  isHidden,
  inCollapsedMode
} = useNavigation()

const target = ref(null)
onClickOutside(target, onClickOutsideNav)
</script>
